import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { useCallback, useContext } from 'react';
import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { timeDuration } from '@atlassian/commerce-telemetry/events';
import * as FieldNames from '../../../common/constants/field-names';
import { createFieldCreditCardStripeError, createFieldCustomValidationError, createGenericCreditCardStripeError, UIAssociations } from '../../../common/utils/errors';
import { useConfirmCardPaymentResultEventDispatch, useConfirmCardSetupResultEventDispatch, useCreatePaymentMethodEventDispatch } from '../../../common/utils/events';
import { createExceptionResult, createSuccessResult, isError, isFailure, isSuccessful } from '../../../common/utils/resultful';
import { sanitizeUrlString } from '../../../common/utils/sanitize-url';
import { formStateRefs } from '../../context';
import { useSetCreditCardFailure } from '../use-credit-card-failure';
import { useCardFields } from '../use-credit-card-fields';
var ERROR_MAPPING = {
  incomplete_number: FieldNames.NUMBER,
  incorrect_number: FieldNames.NUMBER,
  invalid_number: FieldNames.NUMBER,
  incomplete_name: FieldNames.NAME,
  incomplete_expiry: FieldNames.EXPIRY,
  invalid_expiry_year: FieldNames.EXPIRY,
  invalid_expiry_month_past: FieldNames.EXPIRY,
  invalid_expiry_year_past: FieldNames.EXPIRY,
  incomplete_cvc: FieldNames.CVC,
  incorrect_cvc: FieldNames.CVC,
  invalid_cvc: FieldNames.CVC
};
var useFailureResultUIHandlerWrapper = function useFailureResultUIHandlerWrapper(callback) {
  var setFailure = useSetCreditCardFailure();
  var _useContext = useContext(formStateRefs),
    _useContext2 = _slicedToArray(_useContext, 1),
    refs = _useContext2[0];
  var trackedAction = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
    var result,
      ref,
      _args = arguments;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          setFailure(undefined);
          _context.next = 3;
          return callback.apply(void 0, _args);
        case 3:
          result = _context.sent;
          if (!isSuccessful(result)) {
            _context.next = 6;
            break;
          }
          return _context.abrupt("return", result);
        case 6:
          if (isError(result) && result.error.association.type === UIAssociations.BindingLevels.INPUT) {
            // TODO: Refactor this to be castless
            ref = refs[result.error.association.name];
            ref.setFailure(result);
            ref.focus();
          } else {
            setFailure(result);
          }
          return _context.abrupt("return", result);
        case 8:
        case "end":
          return _context.stop();
      }
    }, _callee);
  })), [setFailure, callback, refs]);
  return trackedAction;
};
// TODO: Put this in another file or package
export var useTryCatchToExceptionResultWrapper = function useTryCatchToExceptionResultWrapper(callback) {
  var callbackWrapper = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
      var _args2 = arguments;
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            _context2.prev = 0;
            _context2.next = 3;
            return callback.apply(void 0, _args2);
          case 3:
            return _context2.abrupt("return", _context2.sent);
          case 6:
            _context2.prev = 6;
            _context2.t0 = _context2["catch"](0);
            return _context2.abrupt("return", createExceptionResult(_context2.t0));
          case 9:
          case "end":
            return _context2.stop();
        }
      }, _callee2, null, [[0, 6]]);
    }));
    return function callbackWrapper() {
      return _ref2.apply(this, arguments);
    };
  }();
  var trackedAction = useFailureResultUIHandlerWrapper(callbackWrapper);
  return trackedAction;
};
var wrapHookWithSubmissionWrappers = function wrapHookWithSubmissionWrappers(useEventDispatch, useSubmitHook) {
  var useWrappedSubmitHook = function useWrappedSubmitHook() {
    var submit = useSubmitHook.apply(void 0, arguments);
    var wrappedSubmit = useTryCatchToExceptionResultWrapper(useFailureResultUIHandlerWrapper(useTryCatchToExceptionResultWrapper(submit)));
    var dispatchEvent = useEventDispatch();
    var eventDispatchedSubmit = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
      var _len,
        submitArgs,
        _key,
        timingData,
        _args3 = arguments;
      return _regeneratorRuntime.wrap(function _callee3$(_context3) {
        while (1) switch (_context3.prev = _context3.next) {
          case 0:
            for (_len = _args3.length, submitArgs = new Array(_len), _key = 0; _key < _len; _key++) {
              submitArgs[_key] = _args3[_key];
            }
            _context3.next = 3;
            return timeDuration(function () {
              return wrappedSubmit.apply(void 0, submitArgs);
            });
          case 3:
            timingData = _context3.sent;
            dispatchEvent(timingData);
            return _context3.abrupt("return", timingData.timedPayload);
          case 6:
          case "end":
            return _context3.stop();
        }
      }, _callee3);
    })), [dispatchEvent, wrappedSubmit]);
    return eventDispatchedSubmit;
  };
  return useWrappedSubmitHook;
};
var stripeResultErrorHandlingWrapper = function stripeResultErrorHandlingWrapper(callback, getStripePayload, createCommercePayload) {
  return /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
    var result,
      mappedErrorField,
      payload,
      _args4 = arguments;
    return _regeneratorRuntime.wrap(function _callee4$(_context4) {
      while (1) switch (_context4.prev = _context4.next) {
        case 0:
          _context4.next = 2;
          return callback.apply(void 0, _args4);
        case 2:
          result = _context4.sent;
          if (!(result.error !== undefined)) {
            _context4.next = 10;
            break;
          }
          mappedErrorField = result.error.code !== undefined ? ERROR_MAPPING[result.error.code] : undefined;
          if (!(mappedErrorField !== undefined)) {
            _context4.next = 9;
            break;
          }
          return _context4.abrupt("return", createFieldCreditCardStripeError(result.error, mappedErrorField));
        case 9:
          return _context4.abrupt("return", createGenericCreditCardStripeError(result.error));
        case 10:
          payload = getStripePayload(result);
          if (!(payload === undefined)) {
            _context4.next = 13;
            break;
          }
          return _context4.abrupt("return", createExceptionResult(new Error('Payload was undefined')));
        case 13:
          return _context4.abrupt("return", createSuccessResult(createCommercePayload(payload)));
        case 14:
        case "end":
          return _context4.stop();
      }
    }, _callee4);
  }));
};
var createPaymentMethod = stripeResultErrorHandlingWrapper(function (stripe, paymentMethodData) {
  return stripe.createPaymentMethod(_objectSpread({
    type: 'card'
  }, paymentMethodData));
}, function (_ref5) {
  var paymentMethod = _ref5.paymentMethod;
  return paymentMethod;
}, function (paymentMethod) {
  return {
    paymentMethod: paymentMethod
  };
});

/**
 * Combines the create payment method API and the confirm card API.
 * Payment method API is used to get CC details.
 * Confirm CC API is used to get the setup intent.
 * @see https://stripe.com/docs/js/payment_intents/confirm_card_payment
 * @see https://stripe.com/docs/js/payment_methods/create_payment_method
 */
var confirmCardSetup = stripeResultErrorHandlingWrapper(function (stripe, intentSecret, paymentMethodId, options) {
  return stripe.confirmCardSetup(intentSecret, {
    payment_method: paymentMethodId
  }, options);
}, function (_ref6) {
  var setupIntent = _ref6.setupIntent;
  return setupIntent;
}, function (setupIntent) {
  return {
    setupIntent: setupIntent
  };
});
var confirmCardPayment = stripeResultErrorHandlingWrapper(function (stripe, intentSecret, paymentMethodId, options) {
  return stripe.confirmCardPayment(intentSecret, paymentMethodId ? {
    payment_method: paymentMethodId
  } : undefined, options);
}, function (_ref7) {
  var paymentIntent = _ref7.paymentIntent;
  return paymentIntent;
}, function (paymentIntent) {
  return {
    paymentIntent: paymentIntent
  };
});
var useBaseCreatePaymentMethod = function useBaseCreatePaymentMethod() {
  var elements = useElements();
  var fields = useCardFields();
  var validateFormAndCreatePaymentMethod = useCallback( /*#__PURE__*/function () {
    var _ref8 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5(stripe, billingDetails) {
      var element, message, errorResult, _message;
      return _regeneratorRuntime.wrap(function _callee5$(_context5) {
        while (1) switch (_context5.prev = _context5.next) {
          case 0:
            if (!(elements === null)) {
              _context5.next = 2;
              break;
            }
            return _context5.abrupt("return", createExceptionResult(new Error('Expected it to be impossible for Stripe not to be loaded at this point')));
          case 2:
            element = elements.getElement(CardNumberElement);
            if (!(element === null)) {
              _context5.next = 7;
              break;
            }
            message = 'Your creditcard number is incomplete.';
            errorResult = createFieldCustomValidationError(message, FieldNames.NUMBER);
            return _context5.abrupt("return", errorResult);
          case 7:
            if (fields.name) {
              _context5.next = 10;
              break;
            }
            _message = 'Your cardholder name is incomplete.';
            return _context5.abrupt("return", createFieldCustomValidationError(_message, FieldNames.NAME, _message));
          case 10:
            return _context5.abrupt("return", createPaymentMethod(stripe, {
              card: element,
              billing_details: _objectSpread(_objectSpread({}, fields), billingDetails),
              metadata: {
                'globalThis.location.href': sanitizeUrlString(globalThis.location.href)
              }
            }));
          case 11:
          case "end":
            return _context5.stop();
        }
      }, _callee5);
    }));
    return function (_x, _x2) {
      return _ref8.apply(this, arguments);
    };
  }(), [elements, fields]);
  return validateFormAndCreatePaymentMethod;
};
var createUseSubmitTransactionHook = function createUseSubmitTransactionHook(useEventDispatch, useSubmitHook) {
  var useTransaction = function useTransaction(transaction) {
    var submit = useSubmitHook();

    // AFP-2511 TODO: Fix automatic suppressions below
    // eslint-disable-next-line react-hooks/exhaustive-deps
    var consumerWrappedSubmit = useCallback(transaction(submit), [submit, transaction]);
    return consumerWrappedSubmit;
  };
  var useSubmitTransaction = wrapHookWithSubmissionWrappers(useEventDispatch, useTransaction);
  return useSubmitTransaction;
};
export var useCreatePaymentMethodWithStripeTransaction = createUseSubmitTransactionHook(useCreatePaymentMethodEventDispatch, useBaseCreatePaymentMethod);
var useBaseConfirmCardSetup = function useBaseConfirmCardSetup() {
  return confirmCardSetup;
};
export var useConfirmCardSetupWithStripeTransaction = createUseSubmitTransactionHook(useConfirmCardSetupResultEventDispatch, useBaseConfirmCardSetup);
var useBaseConfirmCardPayment = function useBaseConfirmCardPayment() {
  return confirmCardPayment;
};
export var useConfirmCardPaymentWithStripeTransaction = createUseSubmitTransactionHook(useConfirmCardPaymentResultEventDispatch, useBaseConfirmCardPayment);
var wrapUseTransactionInFormStripe = function wrapUseTransactionInFormStripe(useSubmitWithStripeTransaction) {
  var useSubmitTransaction = function useSubmitTransaction(transactionCallback) {
    var stripe = useStripe();
    var newSubmit = useSubmitWithStripeTransaction(useCallback(function (submit) {
      return transactionCallback(function () {
        for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
          args[_key2] = arguments[_key2];
        }
        return submit.apply(void 0, [stripe].concat(args));
      });
    }, [stripe, transactionCallback]));
    return stripe !== null ? newSubmit : null;
  };
  return useSubmitTransaction;
};
export var useCreatePaymentMethodTransaction = wrapUseTransactionInFormStripe(useCreatePaymentMethodWithStripeTransaction);
export var useConfirmCardSetupTransaction = wrapUseTransactionInFormStripe(useConfirmCardSetupWithStripeTransaction);
export var useConfirmCardPaymentTransaction = wrapUseTransactionInFormStripe(useConfirmCardPaymentWithStripeTransaction);
/**
 * Utility method that combines your {@link UseCreatePaymentMethod} and {@link UseConfirmCard} hooks together
 * so you don't have to do so manually.
 */
export var combineCreatePaymentMethodAndConfirmCardHooks = function combineCreatePaymentMethodAndConfirmCardHooks(useCreatePaymentMethod, useConfirmCard) {
  var useCreatePaymentMethodAndConfirmCard = function useCreatePaymentMethodAndConfirmCard() {
    var createPaymentMethod = useCreatePaymentMethod();
    var confirmCard = useConfirmCard();
    var confirm = useCallback( /*#__PURE__*/function () {
      var _ref9 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6(options) {
        var paymentMethodResult, confirmCardResult;
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) switch (_context6.prev = _context6.next) {
            case 0:
              _context6.next = 2;
              return createPaymentMethod();
            case 2:
              paymentMethodResult = _context6.sent;
              if (!isFailure(paymentMethodResult)) {
                _context6.next = 5;
                break;
              }
              return _context6.abrupt("return", paymentMethodResult);
            case 5:
              _context6.next = 7;
              return confirmCard(paymentMethodResult.payload.paymentMethod.id, options);
            case 7:
              confirmCardResult = _context6.sent;
              if (!isFailure(confirmCardResult)) {
                _context6.next = 10;
                break;
              }
              return _context6.abrupt("return", confirmCardResult);
            case 10:
              return _context6.abrupt("return", createSuccessResult(_objectSpread(_objectSpread({}, paymentMethodResult.payload), confirmCardResult.payload)));
            case 11:
            case "end":
              return _context6.stop();
          }
        }, _callee6);
      }));
      return function (_x3) {
        return _ref9.apply(this, arguments);
      };
    }(), [createPaymentMethod, confirmCard]);
    return createPaymentMethod !== null && confirmCard !== null ? confirm : null;
  };
  return useCreatePaymentMethodAndConfirmCard;
};